import React from 'react';
import { Button, Popover, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { SmileOutlined } from '@ant-design/icons';
import { commonIconButtonStyle } from '../../../../Shared/Styles/iconButton';

// Lazy-load the heavy emoji picker
const EmojiPickerLazy = React.lazy(() => import(/* webpackPrefetch: true */ 'emoji-picker-react'));

// Memoized emoji panel
const EmojiPanel = React.memo(
    ({ isDarkMode, onEmojiClick, loadingLabel, width = '100%', height = 320, emojiSize = 24 }) => (
        <React.Suspense fallback={<div style={{ width, height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{loadingLabel}</div>}>
            <EmojiPickerLazy
                onEmojiClick={onEmojiClick}
                theme={isDarkMode ? 'dark' : 'light'}
                searchDisabled
                skinTonesDisabled
                lazyLoadEmojis
                width={width}
                height={height}
                previewConfig={{ showPreview: false }}
                style={{ '--epr-emoji-size': `${emojiSize}px` }}
            />
        </React.Suspense>
    ),
    (prev, next) => (
        prev.isDarkMode === next.isDarkMode &&
        prev.onEmojiClick === next.onEmojiClick &&
        prev.loadingLabel === next.loadingLabel &&
        prev.width === next.width &&
        prev.height === next.height &&
        prev.emojiSize === next.emojiSize
    )
);

const FooterEmoji = ({
    isMobile,
    emojiOpen,
    setEmojiOpen,
    handleEmojiButtonClick,
    ensureEmojiPreloaded,
    handleEmojiSelect,
    pendingVoice,
    isRecording,
    isDarkMode,
    mobileButtonStyle,
    buttonStyle, // New prop
    ICON_SIZE,
    iconSize,
    renderPickerOnly = false
}) => {
    const { t } = useTranslation();

    const tt = (key, fallback) => {
        const value = t(key);
        return value === key ? fallback : value;
    };

    const finalButtonStyle = buttonStyle || (isMobile ? mobileButtonStyle : commonIconButtonStyle);

    if (pendingVoice || isRecording) return null;

    if (isMobile) {
        // On mobile with renderPickerOnly, show only the picker panel (button is rendered in parent)
        if (renderPickerOnly) {
            if (!emojiOpen) return null;

            return (
                <div style={{
                    width: '100%',
                    marginTop: 8,
                    borderTop: `1px solid ${isDarkMode ? '#303030' : '#eff3f4'}`,
                    paddingTop: 8,
                    background: isDarkMode ? '#1f1f1f' : '#fff'
                }}>
                    <div style={{ maxWidth: 520, margin: '0 auto' }}>
                        <EmojiPanel isDarkMode={isDarkMode} onEmojiClick={handleEmojiSelect} loadingLabel={t('loading')} width={'100%'} height={220} emojiSize={20} />
                    </div>
                </div>
            );
        }

        // Mobile: just render the button (picker panel is rendered separately)
        return (
            <Button
                aria-label={tt('emoji', 'Emoji')}
                icon={<SmileOutlined style={{ fontSize: iconSize }} />}
                type="text"
                onClick={handleEmojiButtonClick}
                onMouseEnter={ensureEmojiPreloaded}
                onFocus={ensureEmojiPreloaded}
                style={{ ...finalButtonStyle, color: '#faad14' }}
            />
        );
    }

    return (
        <Popover
            destroyTooltipOnHide={false}
            content={<div style={{ width: 320 }}><EmojiPanel isDarkMode={isDarkMode} onEmojiClick={handleEmojiSelect} loadingLabel={t('loading')} width={320} height={280} emojiSize={22} /></div>}
            trigger="click"
            open={emojiOpen}
            onOpenChange={setEmojiOpen}
            placement="topRight"
        >
            <Tooltip title={tt('emoji', 'Emoji')}>
                <Button
                    aria-label={tt('emoji', 'Emoji')}
                    icon={<SmileOutlined style={{ fontSize: ICON_SIZE }} />}
                    type="text"
                    onMouseEnter={ensureEmojiPreloaded}
                    onFocus={ensureEmojiPreloaded}
                    style={{ ...finalButtonStyle, color: '#faad14' }}
                />
            </Tooltip>
        </Popover>
    );
};

export default FooterEmoji;
